import React, { useEffect, useState } from 'react';
import { X, Download, Share, Plus, Smartphone } from 'lucide-react';

const DISMISS_KEY = 'cv_pwa_install_dismissed';
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 jours

function isIOS() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  // iPhone, iPad, iPod — iOS 13+ iPad reports as MacIntel + touch
  return /iPad|iPhone|iPod/.test(ua) || (ua.includes('Mac') && 'ontouchend' in document);
}

function isStandalone() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

function wasRecentlyDismissed() {
  try {
    const v = localStorage.getItem(DISMISS_KEY);
    if (!v) return false;
    const ts = parseInt(v, 10);
    return !Number.isNaN(ts) && Date.now() - ts < DISMISS_DURATION_MS;
  } catch (_) {
    return false;
  }
}

export default function PWAInstallBanner() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHelp, setIosHelp] = useState(false);

  useEffect(() => {
    // Skip everything if already installed or recently dismissed
    if (isStandalone() || wasRecentlyDismissed()) return;

    // Android / desktop Chrome / Edge — beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      // Delay a few seconds so the banner doesn't pop instantly
      setTimeout(() => setShow(true), 4000);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // iOS Safari — no beforeinstallprompt, show our own help banner
    if (isIOS()) {
      setTimeout(() => setShow(true), 6000);
    }

    // Hide if installed during session
    const onInstalled = () => {
      setShow(false);
      try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) {}
    };
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const dismiss = () => {
    setShow(false);
    setIosHelp(false);
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) {}
  };

  const install = async () => {
    if (deferred) {
      try {
        deferred.prompt();
        const choice = await deferred.userChoice;
        if (choice?.outcome === 'accepted') {
          try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) {}
        }
      } catch (_) {}
      setDeferred(null);
      setShow(false);
    } else if (isIOS()) {
      setIosHelp(true);
    }
  };

  if (!show) return null;

  // iOS help sheet
  if (iosHelp) {
    return (
      <>
        <div className="fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm" onClick={dismiss} />
        <div
          data-testid="pwa-ios-help"
          className="fixed bottom-0 inset-x-0 z-[91] bg-[#0d0d0e] border-t border-white/10 rounded-t-3xl p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] shadow-[0_-20px_60px_rgba(0,0,0,0.6)]"
          role="dialog"
          aria-modal="true"
        >
          <button
            data-testid="pwa-ios-close"
            onClick={dismiss}
            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/[0.05] border border-white/10 flex items-center justify-center text-white/70 hover:bg-white/[0.1]"
            aria-label="Fermer"
          >
            <X size={15} />
          </button>
          <div className="w-10 h-1 rounded-full bg-white/15 mx-auto mb-4" />
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold mb-1">Installation sur iPhone / iPad</div>
          <h3 className="font-serif-editorial text-white text-[22px] leading-tight mb-5">Ajouter Code Voiture à l'écran d'accueil</h3>
          <ol className="space-y-4 text-white/85 text-[14px]">
            <li className="flex items-start gap-3">
              <span className="w-7 h-7 rounded-full bg-red-600 text-white text-[12px] font-bold flex items-center justify-center shrink-0 mt-0.5">1</span>
              <span>Appuie sur l'icône <Share size={14} className="inline -mt-0.5 mx-1 text-red-400" /> <strong className="text-white">Partager</strong> en bas de Safari.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-7 h-7 rounded-full bg-red-600 text-white text-[12px] font-bold flex items-center justify-center shrink-0 mt-0.5">2</span>
              <span>Choisis <Plus size={14} className="inline -mt-0.5 mx-1 text-red-400" /> <strong className="text-white">Sur l'écran d'accueil</strong> dans la liste.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-7 h-7 rounded-full bg-red-600 text-white text-[12px] font-bold flex items-center justify-center shrink-0 mt-0.5">3</span>
              <span>Confirme avec <strong className="text-white">Ajouter</strong>. L'icône Code Voiture apparaît comme une vraie app.</span>
            </li>
          </ol>
          <p className="text-white/45 text-[12px] mt-5">L'app fonctionnera comme une application native, sans barre Safari.</p>
        </div>
      </>
    );
  }

  // Compact install banner (above mobile bottom nav)
  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed left-3 right-3 md:left-auto md:right-6 md:bottom-6 md:w-[380px] z-30 rounded-2xl border border-red-700/40 bg-gradient-to-br from-[#1a0a0a] via-[#100808] to-[#0a0a0b] shadow-2xl shadow-red-950/40 backdrop-blur"
      style={{ bottom: 'calc(76px + env(safe-area-inset-bottom))' }}
    >
      <div className="p-4 pr-3 flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-red-600/20 border border-red-700/40 flex items-center justify-center text-red-300 shrink-0">
          <Smartphone size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] text-red-400 font-bold mb-0.5">Application Code Voiture</div>
          <div className="text-white text-[13.5px] font-semibold leading-tight">Installer l'app sur ton téléphone</div>
          <div className="text-white/55 text-[11.5px] mt-0.5">Accès 1-clic · sans store · hors-ligne partiel</div>
          <div className="mt-3 flex items-center gap-2">
            <button
              data-testid="pwa-install-btn"
              onClick={install}
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-red-600 hover:bg-red-500 text-white text-[12.5px] font-semibold shadow-lg shadow-red-900/40"
            >
              <Download size={13} /> Installer
            </button>
            <button
              data-testid="pwa-install-later"
              onClick={dismiss}
              className="h-9 px-3 rounded-full text-white/55 hover:text-white text-[12px]"
            >
              Plus tard
            </button>
          </div>
        </div>
        <button
          onClick={dismiss}
          aria-label="Fermer"
          className="text-white/40 hover:text-white shrink-0 -mr-1 -mt-1 w-7 h-7 rounded-full flex items-center justify-center"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
